// Phase 25 iter-5e / iter-11 — Card no ProfilePage que aglutina o setup que
// un gandeiro precisa antes de poder vender. iter-10 engadeu o paso "Animal
// con LeyroSpot" para deixar claro que a venda só se pode iniciar dende a
// app móbil. iter-11 lee o `farm_summary` do backend en vez de fiarse do
// `user.farm_id` do JWT (que sempre é null), polo que o cartón reflicte a
// Farm que o gandeiro creou na app durante o onboarding.

import { Banknote, Building2, CheckCircle2, ExternalLink, Smartphone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import type { ConnectStatusResponse } from '@leyrochain/shared';

import { getConnectStatus, startConnectOnboarding } from '../../api/connect';
import { describeAxiosError } from '../../api/errors';
import Spinner from '../ui/Spinner';
import { useToast } from '../../context/ToastContext';

export function SellerSetupCard(): JSX.Element {
  const { addToast } = useToast();
  const [status, setStatus] = useState<ConnectStatusResponse | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    getConnectStatus()
      .then(setStatus)
      .catch((e) => {
        // 503 cando Stripe non está configurado — non é un erro do user.
        const code = (e as { response?: { status?: number } }).response?.status;
        if (code !== 503) {
          setStatusError(describeAxiosError(e, 'Non se puido cargar o estado bancario.'));
        }
      })
      .finally(() => setLoading(false));
  }, []);

  async function handleStart() {
    setStarting(true);
    try {
      const { onboarding_url } = await startConnectOnboarding();
      // Mesmo motivo que en KycPage: navegar coa mesma pestana evita
      // o popup blocker dos browsers móbiles tras `await`. Stripe redirixe
      // a STRIPE_CONNECT_RETURN_URL ao rematar.
      addToast('Abrindo a pasarela de pago…', 'default');
      window.location.assign(onboarding_url);
    } catch (err) {
      addToast(describeAxiosError(err, 'Non se puido iniciar a conta bancaria.'), 'error');
      setStarting(false);
    }
  }

  const farmDone = !!status?.has_farm;
  const farmSummary = status?.farm_summary ?? null;
  const bankDone = !!status?.bank_verified && !!status?.payouts_enabled;
  const leyrospotDone = !!status?.has_leyrospot_animal;
  const allReady = farmDone && bankDone && leyrospotDone;

  return (
    <section className="border border-border rounded-2xl p-6 bg-white">
      <div className="flex items-center gap-2 mb-4">
        <Building2 size={18} className="text-muted" />
        <h3 className="font-semibold text-dark text-base">Setup como vendedor/a</h3>
      </div>

      <p className="text-sm text-muted mb-4">
        Para poder pór un animal á venda precisas tres cousas: a explotación rexistrada, a conta
        bancaria conectada para recibir pagamentos e, polo menos, un animal con LeyroSpot. A
        configuración da venda en si faise dende a <strong>app móbil</strong>.
      </p>

      <ol className="space-y-3">
        <SetupStep
          done={farmDone}
          icon={<Building2 size={16} className="shrink-0" />}
          label={
            farmDone && farmSummary ? (
              <span>
                Explotación: <strong className="text-dark">{farmSummary.name}</strong>
                <span className="text-muted"> · {farmSummary.municipality}</span>
              </span>
            ) : (
              'Explotación rexistrada'
            )
          }
          action={
            farmDone ? (
              <Link
                to="/marketplace/profile/farm"
                className="text-rauxa font-semibold hover:underline"
              >
                Editar →
              </Link>
            ) : (
              <span className="text-xs text-muted italic">Configúraa na app</span>
            )
          }
          extra={
            !farmDone && !loading ? (
              <p className="text-xs text-muted mt-1 leading-relaxed">
                A explotación créase como primeiro paso ao abrir a app móbil de LeyroChain co teu
                rol de gandeiro/a.
              </p>
            ) : null
          }
        />
        <SetupStep
          done={bankDone}
          icon={<Banknote size={16} className="shrink-0" />}
          label="Conta bancaria (Stripe Connect)"
          action={
            loading ? (
              <Spinner size={14} />
            ) : statusError ? (
              <span className="text-xs text-danger">{statusError}</span>
            ) : status === null ? (
              <span className="text-xs text-muted italic">Stripe non configurado</span>
            ) : (
              <button
                type="button"
                onClick={() => void handleStart()}
                disabled={starting}
                className="text-rauxa font-semibold hover:underline inline-flex items-center gap-1 disabled:opacity-60"
              >
                {starting ? (
                  <Spinner size={14} />
                ) : bankDone ? (
                  <>
                    Xestionar conta <ExternalLink size={12} />
                  </>
                ) : (
                  <>
                    {status.has_account ? 'Continuar Stripe' : 'Conectar Stripe'}{' '}
                    <ExternalLink size={12} />
                  </>
                )}
              </button>
            )
          }
          extra={
            status && !bankDone && status.requirements.length > 0 ? (
              <p className="text-xs text-muted mt-1">
                Pendente en Stripe: {status.requirements.slice(0, 2).join(', ')}
                {status.requirements.length > 2 ? '…' : ''}
              </p>
            ) : null
          }
        />
        <SetupStep
          done={leyrospotDone}
          icon={<Smartphone size={16} className="shrink-0" />}
          label="Animal con LeyroSpot rexistrado"
          action={
            <span className="text-xs text-muted italic">
              {leyrospotDone ? 'Pronto desde a app' : 'Escanea desde a app'}
            </span>
          }
          extra={
            !leyrospotDone ? (
              <p className="text-xs text-muted mt-1 leading-relaxed">
                Descarga LeyroChain no móbil, vinculá un crotal NFC ao animal e dende a páxina do
                animal pulsa <em>Pór á venda</em>.
              </p>
            ) : null
          }
        />
      </ol>

      {allReady && (
        <div className="mt-5 flex items-start gap-3 rounded-xl border border-rauxa/30 bg-rauxa/5 p-4">
          <CheckCircle2 size={18} className="text-rauxa shrink-0 mt-0.5" />
          <p className="text-sm text-dark leading-relaxed">
            <strong>Listo.</strong> Pode pór animais á venda dende a app móbil — abre o detalle do
            animal e pulsa <em>Pór á venda</em>.
          </p>
        </div>
      )}
    </section>
  );
}

function SetupStep({
  done,
  label,
  icon,
  action,
  extra,
}: {
  done: boolean;
  /** Plain string or rich JSX (Farm step renders nome + município inline). */
  label: React.ReactNode;
  /**
   * Step-specific glyph (Building2 / Banknote / Smartphone). Replaced by a
   * green check when the step is `done`.
   */
  icon: React.ReactNode;
  action: React.ReactNode;
  extra?: React.ReactNode;
}) {
  return (
    <li className="flex items-center justify-between gap-3 p-3 rounded-xl bg-subtle">
      <div className="flex items-center gap-2 min-w-0">
        {done ? (
          <CheckCircle2 size={16} className="text-rauxa shrink-0" />
        ) : (
          <span className="text-muted">{icon}</span>
        )}
        <div className="min-w-0">
          <span className={`text-sm font-medium ${done ? 'text-dark' : 'text-muted'}`}>
            {label}
          </span>
          {extra}
        </div>
      </div>
      <div className="shrink-0">{action}</div>
    </li>
  );
}
